'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useLocale,
  useBlockPart,
  type MarketplaceCatalogCard,
  type MarketplaceBookmarkKind,
} from '@akademi/site-builder-runtime';
import { Button, Card, CardContent, Separator, Skeleton } from '@akademi/ui';
import { ShoppingCart, X, Heart, Clock } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { textTertiary } from '../_shared/styles';
import { formatMarketPrice, mpSurface, type MarketPrice } from './_shared';
import { useMarketplaceCart } from './_useMarketplaceCart';

export interface MarketplaceCoursePurchaseBoxProps {
  /** The course (or catalog/bundle) id — akademi-lms-v2's
   *  `marketplaceItemId`. Required for the live add/remove-cart calls; with
   *  no source mounted it's ignored and the toggle is local-only. */
  marketplaceItemId?: string;
  price?: MarketPrice;
  listPrice?: MarketPrice;
  free?: boolean;
  /** Hide this box's own price/free display — for a host page (e.g. the
   *  course-detail sidebar) that already shows the price itself, so the
   *  two don't stack redundantly. */
  showPrice?: boolean;
  /** Fetch `getCatalog(marketplaceItemId)` and let it override `price` /
   *  `listPrice` / `free` once it resolves — on by default, since a
   *  seeded/authored price on the reserved marketplace-catalog-detail page
   *  goes stale the moment the real catalog's price changes. Set to
   *  `false` for a host that already resolved the right price itself and
   *  whose `marketplaceItemId` isn't a catalog id in the first place (e.g.
   *  the course-detail sidebar's `CourseDetailPurchaseBox`, which passes a
   *  genuine LMS course id). */
  deriveFromCatalog?: boolean;
  inCartDefault?: boolean;
  cartHref?: string;
  /** Show the watch-later/favourite buttons. Only ever actually renders
   *  when `deriveFromCatalog` is `false` — there's no catalog/bundle-level
   *  bookmark endpoint (akademi-lms-v2's own catalog-detail page has no
   *  such buttons either; confirmed live, the course-bookmark endpoint
   *  rejects a catalog id), so a catalog-context instance would only ever
   *  show buttons that error on click. */
  showWishlistActions?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** i18n key pair for a bookmark toggle's success toast, per direction. */
const BOOKMARK_MESSAGES: Record<MarketplaceBookmarkKind, { added: string; removed: string }> = {
  'watch-later': {
    added: 'marketplace.purchaseBox.addedToWatchLater',
    removed: 'marketplace.purchaseBox.removedFromWatchLater',
  },
  favourites: {
    added: 'marketplace.purchaseBox.addedToFavourites',
    removed: 'marketplace.purchaseBox.removedFromFavourites',
  },
};

/**
 * Single-course "buy this" sidebar widget — akademi-lms-v2's
 * `CommerceContent` (course detail page). With a {@link useMarketplaceCart}
 * source mounted and a `marketplaceItemId`, add/remove hit the live
 * `/marketplace/cart` endpoint and `inCart` reflects the real cart; with
 * no source, `inCartDefault` seeds a local-only toggle — the "fallbacks
 * stay, for testing" contract.
 *
 * <p>By default ({@link MarketplaceCoursePurchaseBoxProps.deriveFromCatalog
 * `deriveFromCatalog`}), this fetches `getCatalog(marketplaceItemId)`
 * itself — the same envelope {@link MarketplaceCatalogHero} reads — and
 * lets it override `price`/`listPrice`/`free` once resolved. That live
 * value wins even when the host passed an authored `price`: a page's
 * persisted props can carry a stale seeded price from before this block
 * derived it live, and the fetched catalog is always the correct one. That
 * fetch also resolves the catalog's OWN `marketplaceItemId` (akademi-lms-v2's
 * `categoryDetail.marketplaceItemId` — the commerce-layer id `cart.add()`
 * needs) which can differ from the route's content id passed in here, so
 * add/remove-cart use the resolved id once it's known, not the raw prop.
 * `deriveFromCatalog` also gates the wishlist buttons — there's no
 * catalog-level bookmark endpoint, only a per-course one, so they never
 * render in catalog mode. A host whose id isn't a catalog id in the first
 * place (e.g. the course-detail sidebar's `CourseDetailPurchaseBox`, which
 * passes a genuine LMS course id and already resolved its own price) opts
 * out via `deriveFromCatalog={false}`, which also makes its own
 * `showWishlistActions` take effect.
 */
export function MarketplaceCoursePurchaseBox({
  marketplaceItemId,
  price,
  listPrice,
  free = false,
  showPrice = true,
  deriveFromCatalog = true,
  inCartDefault = false,
  cartHref = '/marketplace/cart',
  showWishlistActions = true,
  paddingOuter,
  paddingInner,
}: MarketplaceCoursePurchaseBoxProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const part = useBlockPart();
  const cart = useMarketplaceCart();

  const [catalog, setCatalog] = useState<MarketplaceCatalogCard | null>(null);
  const [catalogStatus, setCatalogStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );

  useEffect(() => {
    if (!deriveFromCatalog || !cart?.source || !marketplaceItemId) return;
    let cancelled = false;
    setCatalogStatus('loading');
    cart.source
      .getCatalog(marketplaceItemId)
      .then((fetched) => {
        if (cancelled) return;
        setCatalog(fetched);
        setCatalogStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setCatalogStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [deriveFromCatalog, cart?.source, marketplaceItemId]);

  const effectiveId = catalog?.id ?? marketplaceItemId;
  const effectivePrice = catalog?.fromPrice ?? price;
  const effectiveListPrice = catalog?.fromListPrice ?? listPrice;
  const effectiveFree = catalog?.freeItem ?? free;
  const catalogLoading = catalogStatus === 'loading';

  const live = Boolean(cart && effectiveId);

  const [localInCart, setLocalInCart] = useState(inCartDefault);
  const [favourite, setFavourite] = useState(false);
  const [favouritePending, setFavouritePending] = useState(false);
  const [watchLater, setWatchLater] = useState(false);
  const [watchLaterPending, setWatchLaterPending] = useState(false);

  const inCart = live ? cart!.inCart(effectiveId!) : localInCart;
  const setInCart = (next: boolean) => {
    if (live) {
      if (next) void cart!.add(effectiveId!).catch(() => {});
      else void cart!.removeByItemId(effectiveId!).catch(() => {});
    } else {
      setLocalInCart(next);
    }
  };

  // No envelope here carries the viewer's CURRENT favourite/watch-later
  // membership (unlike a course-detail payload's own `favourite`/
  // `watchLater` flags) — the toggle always starts unpressed and reflects
  // only what's changed this session. The endpoint itself is a single
  // idempotent PUT that flips membership on each call, same as the
  // course-detail sidebar's own bookmark buttons.
  const toggleWishlist = (
    kind: MarketplaceBookmarkKind,
    current: boolean,
    setState: (v: boolean) => void,
    setPending: (v: boolean) => void,
  ) => {
    const next = !current;
    setState(next);
    if (!live) return;
    setPending(true);
    const messages = BOOKMARK_MESSAGES[kind];
    cart!.source
      .toggleBookmark(effectiveId!, kind)
      .then(() => {
        toast.success(
          t(next ? messages.added : messages.removed, {
            defaultValue: next ? 'Added' : 'Removed',
          }),
        );
      })
      .catch(() => {
        setState(current);
        toast.error(
          t('marketplace.purchaseBox.bookmarkError', {
            defaultValue: "Couldn't complete this action. Please try again.",
          }),
        );
      })
      .finally(() => setPending(false));
  };

  const priceText = formatMarketPrice(effectivePrice, locale);
  const listPriceText = formatMarketPrice(effectiveListPrice, locale);
  const showDiscount = Boolean(listPriceText && priceText && listPriceText !== priceText);

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardContent
          className="p-0"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}
        >
          {showPrice && catalogLoading ? (
            <Skeleton style={{ height: 52, borderRadius: 'var(--radius-md, 8px)' }} />
          ) : (
            showPrice &&
            (effectiveFree ? (
              <div
                style={{
                  border: '1px solid var(--color-success-500, #10b981)',
                  borderRadius: 'var(--radius-md, 8px)',
                  padding: 'var(--space-3, 12px)',
                  textAlign: 'center',
                  color: 'var(--color-success-600, #059669)',
                  fontWeight: 700,
                }}
              >
                {t('marketplace.purchaseBox.free')}
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
                  borderRadius: 'var(--radius-md, 8px)',
                  padding: 'var(--space-3, 12px)',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 'var(--space-2, 8px)',
                }}
              >
                <strong
                  style={{
                    fontSize: 'var(--font-size-2xl, 24px)',
                    color: 'var(--color-text-primary, #18181b)',
                  }}
                >
                  {priceText}
                </strong>
                {showDiscount && (
                  <span style={{ ...textTertiary, textDecoration: 'line-through' }}>
                    {listPriceText}
                  </span>
                )}
              </div>
            ))
          )}

          {inCart ? (
            <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }} {...part('actions')}>
              <Button
                type="button"
                variant="outline"
                style={{ flex: 1 }}
                disabled={catalogLoading}
                onClick={() => setInCart(false)}
              >
                <X size={16} /> {t('marketplace.purchaseBox.removeFromCart')}
              </Button>
              <Button asChild variant="brand" style={{ flex: 1 }}>
                <a href={cartHref}>{t('marketplace.purchaseBox.goToCart')}</a>
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="brand"
              style={{ width: '100%' }}
              disabled={catalogLoading}
              onClick={() => setInCart(true)}
              {...part('add-to-cart')}
            >
              <ShoppingCart size={16} /> {t('marketplace.purchaseBox.addToCart')}
            </Button>
          )}

          {showWishlistActions && !deriveFromCatalog && (
            <>
              <Separator />
              <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={watchLaterPending}
                  aria-pressed={watchLater}
                  onClick={() =>
                    toggleWishlist('watch-later', watchLater, setWatchLater, setWatchLaterPending)
                  }
                  style={{ color: watchLater ? 'var(--color-brand-600, #287f96)' : undefined }}
                >
                  <Clock size={16} /> {t('marketplace.purchaseBox.watchLater')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={favouritePending}
                  aria-pressed={favourite}
                  onClick={() =>
                    toggleWishlist('favourites', favourite, setFavourite, setFavouritePending)
                  }
                  style={{ color: favourite ? 'var(--color-error-500, #ef4444)' : undefined }}
                >
                  <Heart size={16} fill={favourite ? 'currentColor' : 'none'} />{' '}
                  {t('marketplace.purchaseBox.favourite')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
